import type { Queue, Tag } from 'lib-shared-legacy-types';

import { ChatbotType } from 'apis/chatbots';
import { BusinessHoursNode } from 'components/powerFlowShared/components/modals/businesshours/businesshours';
import type { FlowNodeTagT } from 'components/powerFlowShared/components/nodeTypes';
import type {
  FlowNodeTypes,
  OptionT,
} from 'components/powerFlowShared/components/types';
import type { NodeT } from 'fable/flowEditor';

// Init ------------------------------------------------------------------------

export type FlowMessengerNodeInit = NodeT<FlowNodeTypes.INIT, null>;

// Queue ------------------------------------------------------------------------

export type FlowMessengerNodeQueuePayloadT = {
  queue: null | OptionT;
  queue_timeout?: {
    timeout_in_seconds: number;
    timeout_node_id: null | string;
  };
  welcome_message?: null | string;
};
export type FlowMessengerNodeQueue = NodeT<
  FlowNodeTypes.QUEUE,
  FlowMessengerNodeQueuePayloadT
>;

// Contact Form ------------------------------------------------------------------------

export type FlowMessengerNodeContactFormPayload = {
  queue: null | OptionT;
  fromEmail?: string;
};
export type FlowMessengerNodeContactForm = NodeT<
  FlowNodeTypes.CONTACT_FORM,
  FlowMessengerNodeContactFormPayload
>;

// Chat Input ------------------------------------------------------------------------

export type FlowMessengerNodeChatInputPayload = {
  field_label: string;
  field_placeholder: string;
  message: string;
  nextNodeId: string;
};
export type FlowMessengerNodeChatInput = NodeT<
  FlowNodeTypes.CHAT_INPUT,
  FlowMessengerNodeChatInputPayload
>;

// Messenger Menu ------------------------------------------------------------------------

export type Option = {
  id: string;
  label: string;
  nextNodeId: string;
};
export type FlowMessengerNodeMessengerMenuPayload = {
  options: Option[];
  text: string;
};
export type FlowMessengerNodeMessengerMenu = NodeT<
  FlowNodeTypes.MESSENGER_MENU,
  FlowMessengerNodeMessengerMenuPayload
>;

// Messenger Message ------------------------------------------------------------------------

export type FlowMessengerNodeMessengerMessagePayload = {
  text: string;
  nextNodeId: string;
};
export type FlowMessengerNodeMessengerMessage = NodeT<
  FlowNodeTypes.MESSENGER_MESSAGE,
  FlowMessengerNodeMessengerMessagePayload
>;

// URL Trigger ------------------------------------------------------------------------

export type Route = {
  urlPrefix: string;
  nextNodeId: string;
};
export type FlowMessengerNodeURLTriggerPayload = {
  defaultNextNodeId: string;
  routes: Route[];
};
export type FlowMessengerNodeURLTrigger = NodeT<
  FlowNodeTypes.URL,
  FlowMessengerNodeURLTriggerPayload
>;

// Availability ------------------------------------------------------------------------

export enum FlowMessengerNodeAvailabilityStrategy {
  NoAgentsAvailable = 'noAgentsAvailable', // messenger will not be available when no agents are available
  NeverAvailable = 'neverAvailable', // messenger will never be available
  AlwaysAvailable = 'alwaysAvailable', // messenger will always be available
}

export type FlowMessengerNodeAvailabilityQuery = {
  type: FlowMessengerNodeAvailabilityStrategy;
};

export interface FlowMessengerNodeAvailabilityPayload {
  availableNextNodeId: string;
  notAvailableNextNodeId: string;
  query: FlowMessengerNodeAvailabilityQuery;
}

export type FlowMessengerNodeAvailability = NodeT<
  FlowNodeTypes.MESSENGER_AVAILABILITY,
  FlowMessengerNodeAvailabilityPayload
>;

// Chatbot ------------------------------------------------------------------------

export interface FlowMessengerNodeChatBotPayload {
  successNextNodeId?: string;
  failNextNodeId?: string;
  chatbotId: string;
  chatbotName?: string;
  additionalSettings?: {
    chatbotType?: ChatbotType;
  }
}

export type FlowMessengerNodeChatBot = NodeT<
  FlowNodeTypes.MESSENGER_CHAT_BOT,
  FlowMessengerNodeChatBotPayload
>;

// Custom forms -------------------------------------------------------------------

export interface FlowMessengerNodeCustomFormPayload {
  formId: string;
  formTitle: string;
  nextNodeId: string;
}

export type FlowMessengerNodeCustomForm = NodeT<
  FlowNodeTypes.MESSENGER_FORM,
  FlowMessengerNodeCustomFormPayload
>;

// Actions ------------------------------------------------------------------------

export type FlowMessengerAction =
  | FlowMessengerNodeInit
  | FlowMessengerNodeQueue
  | FlowMessengerNodeChatInput
  | FlowMessengerNodeMessengerMenu
  | FlowMessengerNodeMessengerMessage
  | FlowMessengerNodeURLTrigger
  | FlowNodeTagT
  | BusinessHoursNode
  | FlowMessengerNodeCustomForm;

export type FlowMessengerExtraData = {
  name: string;
  queues: Queue[];
  tags: Tag[];
};
